/**
 * useConnectionRequestCount - Hook to fetch pending connection request count
 * ===========================================================================
 * 
 * Fetches count of pending incoming connection requests for badge display.
 * Used by: PrimarySidebar for "Connections" nav item badge
 */

import { useState, useEffect } from 'react';
import { useAuthState } from '@/auth/AuthProvider';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';

interface ConnectionRequest {
  id: string;
  toUserId: string;
  status: string;
}

export function useConnectionRequestCount(caseId?: string) {
  const { user } = useAuthState();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setCount(0);
      return;
    }

    setLoading(true);

    const constraints = [
      where('toUserId', '==', user.uid),
      where('status', '==', 'pending'),
    ] as const;

    const q = caseId
      ? query(collection(db, 'connections'), ...constraints, where('caseId', '==', caseId))
      : query(collection(db, 'connections'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setCount(snapshot.size);
        setLoading(false);
      },
      (error) => {
        console.error('[useConnectionRequestCount] onSnapshot error:', error);
        setCount(0);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user?.uid, caseId]);

  return { count, loading };
}
